import { escapeHtml } from "./escapeHtml.js";

/**
 * `<input type="file" accept>` for short clips embedded as `data:video/…;base64,…` in Max buffers.
 */
export const MAX_BROWSER_VIDEO_FILE_ACCEPT =
  "video/mp4,video/webm,video/ogg,video/quicktime,.mp4,.webm,.mov,.m4v,.ogv,.mkv";

/** Hard cap so Max buffers and preview stay responsive (data URLs expand ~4/3). */
export const MAX_MAX_VIDEO_EMBED_BYTES = 40 * 1024 * 1024;

/** Read a local video file as `data:video/…;base64,…` for embedding in Max. */
export function readVideoFileAsDataUrl(file: File): Promise<string> {
  if (file.size > MAX_MAX_VIDEO_EMBED_BYTES) {
    const mb = Math.round(MAX_MAX_VIDEO_EMBED_BYTES / (1024 * 1024));
    return Promise.reject(new Error(`Video is larger than ${mb} MB — trim the clip or link to an external file.`));
  }
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const out = r.result;
      if (typeof out !== "string") {
        reject(new Error("FileReader did not return a data URL"));
        return;
      }
      if (!/^data:video\//i.test(out)) {
        reject(new Error("Not a recognized video MIME type (try MP4 or WebM)."));
        return;
      }
      resolve(out);
    };
    r.onerror = () => reject(r.error ?? new Error("Failed to read video file"));
    r.readAsDataURL(file);
  });
}

/**
 * Block HTML for Max preview (pass through `markdownToSafeHtmlMaxProse` in Max).
 * Blank lines keep marked/DOM parsers treating this as block-level markup.
 */
export function buildMaxVideoEmbedHtml(title: string, dataUrl: string): string {
  const t = escapeHtml(title.replace(/[\r\n]/g, " ").trim().slice(0, 180)) || "Video";
  const src = escapeHtml(dataUrl);
  return `\n\n<video class="npp-max-embed-video" controls playsinline preload="metadata" title="${t}" src="${src}"></video>\n\n`;
}
